import React, {lazy} from "react";
import {BrowserRouter, Route, Routes} from "react-router-dom";

import "bootstrap/dist/css/bootstrap.min.css";

import {KeycloakContextProvider} from "./context/KeycloakContext";
import {SidebarProvider} from "./context/SidebarContext";
import {WebSocketProvider} from "./context/WebSocketContext.jsx";
import {IconContextProvider} from "./context/IconContext.jsx";
import {ScopeProvider} from './context/ScopeContext.jsx';
import Main from "./sections/Main/Main";
import Welcome from "@sections/Dashboard/Welcome/Welcome";
import Error from "./sections/Error/ErrorPage";
import Error404 from "./sections/Error/ErrorPage404";

// Every route leaf below is lazy: eager imports here dragged swagger, ag-grid,
// maplibre, echarts and the markdown editor into first paint. Only Main,
// Welcome and the two error pages stay eager.
const Dashboard = lazy(() => import("./sections/Dashboard/Dashboard"));
const Settings = lazy(() => import("./sections/Settings/Settings"));
const SettingsAccount = lazy(() => import("./sections/Settings/SettingsAccount/SettingsAccount"));
const SettingsPartners = lazy(() => import("./sections/Settings/SettingsPartners/SettingsPartners"));
const PartnerForm = lazy(() => import("./sections/Settings/SettingsPartners/PartnerForm/PartnerForm"));
const CustomerForm = lazy(() => import("./sections/Settings/SettingsCustomers/CustomerForm/CustomerForm"));
const CustomerDetailsPage = lazy(() =>
    import("./sections/Settings/SettingsCustomers/CustomerDetailsPage/CustomerDetailsPage"));
const SettingsUsers = lazy(() => import("./sections/Settings/SettingsUsers/SettingsUsers"));
const UserForm = lazy(() => import("./sections/Settings/SettingsUsers/UserForm/UserForm"));
const SettingsCustomers = lazy(() => import("./sections/Settings/SettingsCustomers/SettingsCustomers"));
const SettingsRoleMgmt = lazy(() => import("./sections/Settings/SettingsRoleMgmt/SettingsRoleMgmt"));
const RoleForm = lazy(() => import("./sections/Settings/SettingsRoleMgmt/RoleForm/RoleForm"));
const SettingsOrganization = lazy(() => import("./sections/Settings/SettingsOrganization/SettingsOrganization"));
const SettingsAPITokens = lazy(() => import("@sections/Settings/SettingsAPITokens/SettingsAPITokens.jsx"));
const SettingsReportSchedules = lazy(() => import("@sections/Settings/SettingsReportSchedules/SettingsReportSchedules.jsx"));
const AdminTools = lazy(() => import("./sections/AdminTools/AdminTools"));
const CustomersToProducts = lazy(() => import("./sections/AdminTools/CustomersToProducts/CustomersToProducts"));
const JSONCombiner = lazy(() => import("@sections/AdminTools/JSONCombiner/JSONCombiner"));
const Email = lazy(() => import("@sections/AdminTools/Email/Email"));
const NotificationSettings = lazy(() => import("./sections/AdminTools/NotificationSettings/NotificationSettings"));
const Permissions = lazy(() => import("@sections/AdminTools/Permissions/Permissions.jsx"));
const ThemeColors = lazy(() => import("@sections/AdminTools/ThemeColors/ThemeColors.jsx"));
const Paging = lazy(() => import("@sections/AdminTools/Paging/Paging.jsx"));
const Tools = lazy(() => import("@sections/AdminTools/Tools/Tools.jsx"));
const Domains = lazy(() => import("./sections/AdminTools/Domains/Domains.jsx"));
const Scheduling = lazy(() => import("@sections/AdminTools/Scheduling/Scheduling.jsx"));
const SKU = lazy(() => import("./sections/SKU/SKU"));
const InvestigationsMain = lazy(() => import("./sections/Investigations/InvestigationsMain/InvestigationsMain"));
const InvestigationDetails = lazy(() => import("./sections/Investigations/InvestigationDetails/InvestigationDetails"));
const SwaggerDocs = lazy(() => import("./Swagger.jsx"));


function App() {
    return (
        <BrowserRouter>
            <Routes>
                <Route exact path="/error" element={<Error/>}/>
                <Route path="*" element={<Error404/>}/>
                <Route
                    exact
                    path="/"
                    element={
                        <ScopeProvider>
                            <KeycloakContextProvider>
                                <SidebarProvider>
                                    <WebSocketProvider>
                                        <IconContextProvider>
                                            <React.StrictMode>
                                                <Main/>
                                            </React.StrictMode>
                                        </IconContextProvider>
                                    </WebSocketProvider>
                                </SidebarProvider>
                            </KeycloakContextProvider>
                        </ScopeProvider>
                    }
                >
                    <Route
                        exact
                        path="/swagger"
                        element={<SwaggerDocs/>}
                    />
                    <Route exact path="/" element={<Welcome/>}/>
                    <Route
                        exact
                        path="/dashboards/:dashboard_id"
                        element={<Dashboard/>}
                    />
                    <Route exact path="/sku" element={<SKU/>}/>
                    <Route exact path="/settings" element={<Settings/>}>
                        <Route
                            exact
                            path="/settings/account"
                            element={<SettingsAccount/>}
                        />
                        <Route exact path="/settings/users" element={<SettingsUsers/>}/>
                        <Route
                            exact
                            path="/settings/users/new"
                            element={<UserForm/>}
                        />
                        <Route
                            exact
                            path="/settings/users/:userId"
                            element={<UserForm/>}
                        />
                        <Route
                            exact
                            path="/settings/partners"
                            element={<SettingsPartners/>}
                        />
                        <Route
                            exact
                            path="/settings/partners/new"
                            element={<PartnerForm/>}
                        />
                        <Route
                            exact
                            path="/settings/partners/:partnerId"
                            element={<PartnerForm/>}
                        />
                        <Route
                            exact
                            path="/settings/customers"
                            element={<SettingsCustomers/>}
                        />
                        <Route
                            exact
                            path="/settings/customers/new"
                            element={<CustomerForm/>}
                        />
                        <Route
                            exact
                            path="/settings/customers/:customerId"
                            element={<CustomerForm/>}
                        />
                        <Route
                            exact
                            path="/settings/customers/:customerId/service-delivery"
                            element={<CustomerDetailsPage/>}
                        />
                        <Route
                            exact
                            path="/settings/roles"
                            element={<SettingsRoleMgmt/>}
                        />
                        <Route
                            exact
                            path="/settings/roles/new"
                            element={<RoleForm/>}
                        />
                        <Route
                            exact
                            path="/settings/roles/:roleId"
                            element={<RoleForm/>}
                        />
                        <Route
                            exact
                            path="/settings/org"
                            element={<SettingsOrganization/>}
                        />
                        <Route
                            exact
                            path="/settings/tokens"
                            element={<SettingsAPITokens/>}
                        />
                        <Route
                            exact
                            path="/settings/report-schedules"
                            element={<SettingsReportSchedules/>}
                        />
                    </Route>
                    <Route exact path="/admin-tools" element={<AdminTools/>}>
                        <Route
                            exact
                            path="/admin-tools/customers-products"
                            element={<CustomersToProducts/>}
                        />
                        <Route exact path="/admin-tools/email" element={<Email/>}/>
                        <Route exact path="/admin-tools/json" element={<JSONCombiner/>}/>
                        <Route exact path="/admin-tools/notification-settings" element={<NotificationSettings/>}/>
                        <Route exact path="/admin-tools/permissions" element={<Permissions/>}/>
                        <Route exact path="/admin-tools/theme-colors" element={<ThemeColors/>}/>
                        <Route exact path="/admin-tools/paging" element={<Paging/>}/>
                        <Route exact path="/admin-tools/tools" element={<Tools/>}/>
                        <Route exact path="/admin-tools/domains" element={<Domains/>}/>
                        <Route exact path="/admin-tools/scheduling" element={<Scheduling/>}/>
                    </Route>
                    <Route
                        exact
                        path="/investigations"
                        element={<InvestigationsMain/>}
                    />
                    <Route
                        path="/investigations/:investigation_id"
                        element={<InvestigationDetails/>}
                    />
                </Route>
            </Routes>
           
        </BrowserRouter>
    );
}

export default App;